import { faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { FC, useEffect, useRef, useState } from 'react'
import {
  PLATFORM_LABEL,
  PLATFORM_ORDER,
} from 'src/pages/ReviewAnalysis/constants'
import { getPlatformIcon } from 'src/pages/ReviewAnalysis/platformIcons'
import { ReviewPlatform } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import styles from './connectPlatforms.module.scss'

interface PlatformSelectProps {
  onChange: (platform: ReviewPlatform) => void
  value: ReviewPlatform
}

// The platform of one added location: the app's own dropdown, with the
// platform logos, in place of the browser's native select.
export const PlatformSelect: FC<PlatformSelectProps> = ({
  onChange,
  value,
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  const Icon = getPlatformIcon(value)

  return (
    <div className={styles.platformSelect} ref={ref}>
      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        className={classNames(
          styles.platformSelectButton,
          open && styles.platformSelectButtonOpen
        )}
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <Icon className={styles.platformSelectIcon} />
        <span className={styles.platformSelectLabel}>
          {PLATFORM_LABEL[value]}
        </span>
        <FontAwesomeIcon
          className={classNames(
            styles.platformSelectChevron,
            open && styles.platformSelectChevronOpen
          )}
          icon={faChevronDown}
        />
      </button>
      {open && (
        <ul className={styles.platformSelectMenu} role="listbox">
          {PLATFORM_ORDER.map((p) => {
            const OptionIcon = getPlatformIcon(p)
            const on = p === value
            return (
              <li aria-selected={on} key={p} role="option">
                <button
                  className={classNames(
                    styles.platformSelectOption,
                    on && styles.platformSelectOptionOn
                  )}
                  onClick={() => {
                    onChange(p)
                    setOpen(false)
                  }}
                  type="button"
                >
                  <OptionIcon className={styles.platformSelectIcon} />
                  <span className={styles.platformSelectLabel}>
                    {PLATFORM_LABEL[p]}
                  </span>
                  {on && (
                    <FontAwesomeIcon
                      className={styles.platformSelectCheck}
                      icon={faCheck}
                    />
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
